import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { apiInstance } from '../../api/apiInstance';
import { initialState, PasswordResetForm, SignInForm, SignUpForm } from './constants';
import {
  generateVerificationToken,
  verifyEmail,
  verifyEmailViaLink,
  createAccount,
  authenticateUser,
  resendVerificationCode,
  askPasswordReset,
  askPasswordResetFromForm,
  resetPassword,
  refreshToken,
  matchOrg,
  createOrganization,
  joinOrganization,
  resetEmail,
  refreshPassword,
  askEmailChange,
  getDashboardMetrics,
  getActiveBanner,
  confirmEmailChange,
} from './utils';
import { getUserProfile } from '../settings/utils';
import parsePhoneNumberFromString, { CountryCode } from 'libphonenumber-js/min';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const passwordRegex =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&_#^()+=\/\\,.'":;{}[\]<>`~|-])[A-Za-z\d@$!%*?&_#^()+=\/\\,.'":;{}[\]<>`~|-]{8,}$/;

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    updateSignUpFormField(
      state,
      action: PayloadAction<{
        fieldName: keyof SignUpForm;
        value: string | boolean;
      }>,
    ) {
      const { fieldName, value } = action.payload;
      if (fieldName === 'isInvOrg') {
        state.signUpForm[fieldName] = value as boolean;
      } else {
        state.signUpForm[fieldName] = value as string;
      }
    },
    updateSignInFormField(state, action: PayloadAction<{ fieldName: keyof SignInForm; value: string }>) {
      const { fieldName, value } = action.payload;

      state.signInForm[fieldName] = value;
    },
    updatePasswordResetFormField(
      state,
      action: PayloadAction<{
        fieldName: keyof PasswordResetForm;
        value: string | boolean;
      }>,
    ) {
      const { fieldName, value } = action.payload;

      (state.passwordResetForm[fieldName] as typeof value) = value;
    },
    updateVerificationCode(state, action) {
      state.verificationForm.code = action.payload;
    },
    updateResetEmail(state, action) {
      const { value } = action.payload;
      state.resetEmailForm.email = value;
    },
    continueVerification(state, action) {
      const { email, password } = action.payload;

      state.verificationRequested = !state.verificationRequested;
      state.signUpForm.email = email;
      state.signUpForm.password = password;
    },
    continueProfileCreation(state, action) {
      const { email, password } = action.payload;

      state.isVerified = true;
      state.signUpForm.email = email;
      state.signUpForm.password = password;
    },
    clearPasswordError(state) {
      state.signUpFormErrors = {
        ...state.signUpFormErrors,
        passwordErrorType: null,
        passwordError: '',
      };
    },
    clearVerificationError(state) {
      state.verificationFormError.requestError = '';
    },
    handleSignUpSubmit(state) {
      state.signUpFormErrors = initialState.signUpFormErrors;

      const {
        firstName,
        lastName,
        email,
        password,
      } = state.signUpForm;

      const passwordErrorMsg =
        'Password must be at least 8 characters long, with at least 1 lowercase letter, 1 uppercase letter, 1 number, and 1 special character.';

      if (!firstName) {
        state.signUpFormErrors = {
          ...state.signUpFormErrors,
          firstNameError: 'First Name is required',
        };
      }

      if (!lastName) {
        state.signUpFormErrors = {
          ...state.signUpFormErrors,
          lastNameError: 'Last Name is required',
        };
      }

      if (!email) {
        state.signUpFormErrors = {
          ...state.signUpFormErrors,
          emailError: 'Email is required',
        };
      } else if (!emailRegex.test(email)) {
        state.signUpFormErrors = {
          ...state.signUpFormErrors,
          emailError: 'Wrong format',
        };
      }

      if (!password) {
        state.signUpFormErrors = {
          ...state.signUpFormErrors,
          passwordError: 'Password is required.',
        };
      }
      if (!(password.length >= 8)) {
        state.signUpFormErrors = {
          ...state.signUpFormErrors,
          passwordError: passwordErrorMsg,
          passwordErrorType: [...(state.signUpFormErrors.passwordErrorType ?? []), 'length'],
        };
      }
      if (!/[a-z]/.test(password)) {
        state.signUpFormErrors = {
          ...state.signUpFormErrors,
          passwordError: passwordErrorMsg,
          passwordErrorType: [...(state.signUpFormErrors.passwordErrorType ?? []), 'lowercase'],
        };
      }
      if (!/[A-Z]/.test(password)) {
        state.signUpFormErrors = {
          ...state.signUpFormErrors,
          passwordError: passwordErrorMsg,
          passwordErrorType: [...(state.signUpFormErrors.passwordErrorType ?? []), 'uppercase'],
        };
      }
      if (!/[@$!%*?&_#^()+=\/\\,.'":;{}[\]<>`~|-]/.test(password)) {
        state.signUpFormErrors = {
          ...state.signUpFormErrors,
          passwordError: passwordErrorMsg,
          passwordErrorType: [...(state.signUpFormErrors.passwordErrorType ?? []), 'specialChar'],
        };
      }
      if (!/\d/.test(password)) {
        state.signUpFormErrors = {
          ...state.signUpFormErrors,
          passwordError: passwordErrorMsg,
          passwordErrorType: [...(state.signUpFormErrors.passwordErrorType ?? []), 'number'],
        };
      }

      // } else if (!passwordRegex.test(state.signUpForm.password)) {
      //   state.signUpFormErrors = {
      //     ...state.signUpFormErrors,
      //     passwordError:
      //       'Password must be at least 8 characters long, with at least 1 lowercase letter, 1 uppercase letter, 1 number, and 1 special character.',
      //   };
      // }
    },

    handlePasswordResetSubmit(state, action) {
      state.passwordResetFormErrors = initialState.passwordResetFormErrors;
      const formData = state.passwordResetForm;
      if (action.payload) {
        // Step 1 validation: email
        if (!formData.email) {
          state.passwordResetFormErrors = {
            ...state.passwordResetFormErrors,
            emailError: 'Email is required',
          };
        } else if (!emailRegex.test(formData.email)) {
          state.passwordResetFormErrors = {
            ...state.passwordResetFormErrors,
            emailError: 'Invalid Email format',
          };
        }
      } else {
        // Step 2 validation: code + new password
        // When a token is present (link flow), skip code validation — code comes from JWT
        if (!formData.token && !formData.code) {
          state.passwordResetFormErrors = {
            ...state.passwordResetFormErrors,
            requestError: 'Verification code is required',
          };
        } else if (!formData.newPassword) {
          state.passwordResetFormErrors = {
            ...state.passwordResetFormErrors,
            newPasswordError: 'Password is required',
          };
        } else if (!passwordRegex.test(formData.newPassword)) {
          state.passwordResetFormErrors = {
            ...state.passwordResetFormErrors,
            newPasswordError:
              'Password must be at least 8 characters long, with at least 1 lowercase letter, 1 uppercase letter, 1 number, and 1 special character.',
          };
        }
      }
    },
    changeResetEmail(state) {
      state.resetEmailRequested = !state.resetEmailRequested;
    },
    setCurrentUser(state, action) {
      // console.log(action.payload.user)
      state.currentUser = {
        ...action.payload.user,
      };

      // console.log({
      //   addresses: state.currentUser.returnAddresses,
      //   signatures: state.currentUser.signatures,
      // });

      apiInstance.interceptors.request.use(
        (config) => {
          config.headers.Authorization = `Bearer ${action.payload.accessToken}`;
          return config;
        },
        (error) => {
          return Promise.reject(error);
        },
      );
    },
    resetCreatedOrg(state) {
      state.hasCreatedOrganization = false;
    },
    resetCurrentUser(state) {
      state.currentUser = initialState.currentUser;
      state.signInForm = initialState.signInForm;
      localStorage.removeItem('token');
      apiInstance.interceptors.request.clear();
    },
    resetSignUpForm(state) {
      state.signUpForm = initialState.signUpForm;
    },
    resetPasswordResetForm(state) {
      state.passwordResetForm = initialState.passwordResetForm;
      state.passwordResetFormErrors = initialState.passwordResetFormErrors;
    },
    setBannerStatus(state, action) {
      state.bannerStatus = action.payload
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(generateVerificationToken.pending, (state) => {
        if (state.signUpForm.isInvOrg) {
          state.isSendingVerificationCode = false;
        } else {
          state.isSendingVerificationCode = true;
        }
      })
      .addCase(generateVerificationToken.fulfilled, (state, action) => {
        if (action.payload.hasInviteToken) {
          state.isFromInvToken = true;
        } else {
          state.verificationRequested = true;
          state.isSendingVerificationCode = false;
        }
      })
      .addCase(generateVerificationToken.rejected, (state, action) => {
        state.signUpFormErrors.requestError = action.payload
          ? action.payload === 'Organization has to have at least one admin'
            ? 'Action Not Allowed: You are the only admin in your current organization. Please assign another admin before joining new organization'
            : (action.payload as string)
          : 'Something went wrong!';
        state.verificationRequested = false;
        state.isSendingVerificationCode = false;
      })
      .addCase(verifyEmail.pending, (state) => {
        state.isVerifyingEmail = true;
      })
      .addCase(verifyEmail.fulfilled, (state, action) => {
        state.isVerified = true;
        const { signupToken } = action.payload;
        state.signupToken = signupToken;
        state.resetEmailRequested = false;
        state.verificationRequested = false;
        state.isVerifyingEmail = false;
        localStorage.setItem('signupToken', signupToken);
      })
      .addCase(verifyEmail.rejected, (state, action) => {
        state.isVerified = false;
        state.isVerifyingEmail = false;
        state.verificationFormError.requestError = action.payload
          ? (action.payload as string)
          : 'Something went wrong!';
      })
      .addCase(matchOrg.pending, (state) => {
        state.foundOrganization = { ...state.foundOrganization, hasData: null };
      })
      .addCase(matchOrg.fulfilled, (state, action) => {
        const { data } = action.payload;

        state.verificationForm = initialState.verificationForm;
        state.finishedMatchingOrganization = true;

        if (data === null) {
          state.foundOrganization = {
            ...state.foundOrganization,
            hasData: false,
          };
        } else {
          state.foundOrganization = {
            ...state.foundOrganization,
            ...data,
            hasData: true,
          };
        }
      })
      .addCase(createAccount.fulfilled, (state, action) => {
        if (action.payload) {
          localStorage.removeItem('signupToken');
        }
      })
      .addCase(createAccount.rejected, (state, action) => {
        state.defaultSettingsFormErrors.requestError = action.payload
          ? (action.payload as string)
          : 'Something went wrong!';
      })
      .addCase(authenticateUser.pending, (state) => {
        state.isAuthenticating = true;
      })
      .addCase(authenticateUser.fulfilled, (state, { payload }) => {
        const {
          role,
          email,
          firstName,
          lastName,
          accessToken,
          signature,
          returnAddress,
          passwordUpdatedAt,
          courtLocation,
          returnAddresses,
          signatures,
          organization,
          notifications,
          countryCode,
          isSmsAllowed,
          cellPhoneNumber,
          teamRole,
          thirteenTrusteeAddressExclusions,
        } = payload;

        state.currentUser = {
          role,
          email,
          firstName,
          lastName,
          accessToken,
          signature,
          countryCode,
          returnAddress,
          passwordUpdatedAt,
          courtLocation,
          returnAddresses,
          signatures,
          organization,
          notifications,
          isSmsAllowed,
          cellPhoneNumber,
          teamRole,
          thirteenTrusteeAddressExclusions,
        };

        localStorage.setItem('token', accessToken);

        apiInstance.interceptors.request.use(
          (config) => {
            config.headers.Authorization = `Bearer ${accessToken}`;
            return config;
          },
          (error) => {
            return Promise.reject(error);
          },
        );

        state.verificationRequested = false;
        state.isFromInvToken = false;
        state.isVerified = false;
        state.isAuthenticating = false;
        state.signInForm.error = '';
      })
      .addCase(authenticateUser.rejected, (state, action) => {
        if (action.payload) {
          const { message, payload, clientCode } = action.payload as {
            message: string;
            clientCode: string;
            payload: { signupToken: string };
          };
          // These client codes represent valid redirect flows, not user-facing errors
          const isRedirectFlow = clientCode === 'NOT_JOINED_ORGANIZATION' || clientCode === 'EMAIL_NOT_VERIFIED';
          state.signInForm.error = isRedirectFlow ? '' : (message || 'Something went wrong!');
          state.signupToken = payload?.signupToken;
        } else {
          state.signInForm.error = 'Something went wrong!';
        }
        state.isAuthenticating = false;
      })
      .addCase(resendVerificationCode.fulfilled, (state) => {
        state.verificationForm.verificationCodeResend = true;
      })
      .addCase(resendVerificationCode.rejected, (state, action) => {
        state.verificationFormError.requestError = action.payload
          ? (action.payload as string)
          : 'Something went wrong!';
      })
      .addCase(askPasswordReset.fulfilled, (state) => {
        state.passwordResetForm.isRequested = true;
        state.passwordResetForm.isLoading = false;
      })
      .addCase(askPasswordReset.pending, (state) => {
        state.passwordResetForm.isLoading = true;
      })
      .addCase(askPasswordReset.rejected, (state, action) => {
        state.passwordResetFormErrors.requestError = action.payload
          ? (action.payload as string)
          : 'Something went wrong!';
        state.passwordResetForm.isLoading = false;
      })
      .addCase(askPasswordResetFromForm.fulfilled, (state) => {
        state.passwordResetForm.isRequested = true;
        state.passwordResetForm.isLoading = false;
      })
      .addCase(askPasswordResetFromForm.pending, (state) => {
        state.passwordResetForm.isLoading = true;
      })
      .addCase(askPasswordResetFromForm.rejected, (state, action) => {
        state.passwordResetFormErrors.requestError = action.payload
          ? (action.payload as string)
          : 'Something went wrong!';
        state.passwordResetForm.isLoading = false;
      })
      .addCase(resetPassword.fulfilled, () => {
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.passwordResetFormErrors.requestError = action.payload
          ? (action.payload as string)
          : 'Something went wrong!';
      })
      .addCase(refreshToken.fulfilled, (state, action) => {
        const { accessToken } = action.payload;
        localStorage.setItem('token', accessToken);

        apiInstance.interceptors.request.use(
          (config) => {
            config.headers.Authorization = `Bearer ${accessToken}`;
            return config;
          },
          (error) => {
            return Promise.reject(error);
          },
        );
      })
      .addCase(refreshToken.rejected, () => {
        console.error('Token refreshing failed');
      })
      .addCase(confirmEmailChange.fulfilled, (state, action) => {
        const { accessToken, email } = action.payload;
        if (accessToken) {
          localStorage.setItem('token', accessToken);
          apiInstance.interceptors.request.use(
            (config) => {
              config.headers.Authorization = `Bearer ${accessToken}`;
              return config;
            },
            (error) => Promise.reject(error),
          );
        }
        if (email) {
          state.currentUser.email = email;
        }
      })
      .addCase(createOrganization.pending, (state) => {
        state.isCreatingOrganization = true;
      })
      .addCase(createOrganization.fulfilled, (state, action) => {
        state.hasCreatedOrganization = true;
        state.isCreatingOrganization = false;
        state.createOrganizationNetworkError = null;
        state.createOrganizationNetworkErrorStack = null;
        state.signUpForm = initialState.signUpForm;
        state.signInForm = initialState.signInForm;
        state.verificationForm = initialState.verificationForm;
        localStorage.removeItem('signupToken');
      })
      .addCase(createOrganization.rejected, (state, action) => {
        state.hasCreatedOrganization = false;
        state.isCreatingOrganization = false;

        const errorMessages: Record<string, string> = {
          "Domain name can't be used":
            'This domain is currently in use. If it belongs to your organization, please contact Help Desk.',
          "Organization name can't be used":
            'This organization name is currently in use. If it belongs to your organization, please contact Help Desk.',
          'websiteUrl must be a URL address': 'Provided website must be a valid URL address.',
        };

        const { message, errorStack } = action.payload as { message: string; errorStack: string[] };

        const errors = errorStack.filter((error) => errorMessages[error]).map((error) => errorMessages[error]);

        state.createOrganizationNetworkError = errors.length > 0 ? errors.join('\n') : message;
        state.createOrganizationNetworkErrorStack = errorStack || [];
      })
      .addCase(joinOrganization.pending, (state) => {
        state.isRequestingToJoinOrg = true;
      })
      .addCase(joinOrganization.fulfilled, (state, action) => {
        state.requestToJoinOrgSent = true;
        state.isRequestingToJoinOrg = false;
        state.signUpForm = initialState.signUpForm;
        state.signInForm = initialState.signInForm;
        state.verificationForm = initialState.verificationForm;
      })
      .addCase(joinOrganization.rejected, (state) => {
        state.requestToJoinOrgSent = false;
        state.isRequestingToJoinOrg = false;
      })
      .addCase(resetEmail.pending, (state) => {
        state.isSendingVerificationCode = true;
      })
      .addCase(resetEmail.fulfilled, (state) => {
        state.isVerified = false;
        state.verificationRequested = true;
        state.resetEmailFormErrors.requestError = '';
        state.isSendingVerificationCode = false;
      })
      .addCase(resetEmail.rejected, (state, action) => {
        state.resetEmailFormErrors.requestError = action.payload ? (action.payload as string) : 'Something went wrong!';
        state.isVerified = false;
        state.verificationRequested = true;
        state.isSendingVerificationCode = false;
      })
      .addCase(getUserProfile.fulfilled, (state, action) => {
        state.userProfile = action.payload;
      })
      .addCase(askEmailChange.pending, (state) => {
        state.isChangeEmailLoading = true;
      })
      .addCase(askEmailChange.fulfilled, (state) => {
        state.isChangeEmailLoading = false;
      })
      .addCase(askEmailChange.rejected, (state) => {
        state.isChangeEmailLoading = false;
      })
      .addCase(refreshPassword.pending, (state) => {
        state.isPasswordResetLoading = true;
      })
      .addCase(refreshPassword.fulfilled, (state) => {
        state.isPasswordResetLoading = false;
      })
      .addCase(refreshPassword.rejected, (state) => {
        state.isPasswordResetLoading = false;
      })
      .addCase(getDashboardMetrics.fulfilled, (state, action) => {
        state.dashboardMetrics = {
          minutesReclaimed: action.payload.data.minutesReclaimed,
          jobsCompleted: action.payload.data.jobsCompletedCount,
          noticesSent: action.payload.data.noticesSentCount
        }
      })
      .addCase(getActiveBanner.fulfilled, (state, action) => {
        const { activeNotification } = action.payload;
        state.bannerStatus = activeNotification;
        state.hasFailedPayment = activeNotification === 'payment_failed';
      })
      .addCase(verifyEmailViaLink.pending, (state) => {
        state.isVerifyingEmail = true;
        state.verificationFormError.requestError = '';
      })
      .addCase(verifyEmailViaLink.fulfilled, (state, action) => {
        state.isVerified = true;
        const { signupToken } = action.payload;
        state.signupToken = signupToken;
        state.resetEmailRequested = false;
        state.verificationRequested = false;
        state.isVerifyingEmail = false;
        localStorage.setItem('signupToken', signupToken);
      })
      .addCase(verifyEmailViaLink.rejected, (state, action) => {
        state.isVerified = false;
        state.isVerifyingEmail = false;
        state.resetEmailRequested = false;
        state.verificationFormError.requestError = action.payload
          ? (action.payload as string)
          : 'Email verification failed. Please try again.';
      })
  },
});

export const {
  handleSignUpSubmit,
  handlePasswordResetSubmit,
  updateSignUpFormField,
  updateSignInFormField,
  updateVerificationCode,
  updateResetEmail,
  updatePasswordResetFormField,
  clearPasswordError,
  clearVerificationError,
  continueVerification,
  continueProfileCreation,
  changeResetEmail,
  setCurrentUser,
  resetCurrentUser,
  resetCreatedOrg,
  resetSignUpForm,
  resetPasswordResetForm,
  setBannerStatus
} = userSlice.actions;

export default userSlice.reducer;
